import React from "react";
import { useReducerWrapper } from "../../../../shared/hooks/useReducerWrapper";
import { CouponCodeSuccessHandlerArgsType, DispatchInterface, PurchaseTicketArgsType, State } from "./type";
import Reducer from "./reducer";
import { useGetPurchaseTicketForm } from "./dataloader";
import { ApiRegistrationParticipantForParticipantResponse } from "../../../../shared/types/ApiRegistrationParticipantForParticipantResponse";
import TicketStatus from "../../../../shared/enums/TicketStatus";
import { useAuth } from "../../../../shared/context/auth";
import { isInvoicePaymentPending, isPaymentPending, isPaymentReceiptPending, PurchaseTicketStep } from "./util";
import { useQueryClient } from "react-query";
import { refreshDashboardData, updateTicketStatusInDashboardData } from "../../context/dashboard/dataloader";
import { ApiPayForPurchaseOrderResponse } from "../../../../shared/types/ApiPayForPurchaseOrderResponse";
import InventoryType from "../../../../shared/enums/InventoryType";
import { CouponCodeFormFieldsName } from "./enum";
import { useParticipantDetails } from "../../context/participantDetails";
import { DashboardActionType } from "shared/enums/DashboardActionType";
import { InvoicePaymentRequestStatus } from "shared/enums/InvoicePaymentRequestStatus";
import { shouldRefreshPageOnError } from "shared/utils/network";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export default function usePurchaseTicketService({ dismissPurchaseTicketHandler }: PurchaseTicketArgsType) {
  const auth = useAuth();
  const mountRef = React.useRef(true);
  const queryClient = useQueryClient();
  const { participantDetails } = useParticipantDetails();
  const [state, dispatcher] = useReducerWrapper<State, DispatchInterface>(Reducer);
  useLogAnalyticsScreenHook({
    properties: { screenType: AnalyticsScreenNameEnum.PURCHASE_TICKET }
  });
  const { refetch: refetchGetPurchaseTicketForm } = useGetPurchaseTicketForm({
    registrationParticipantId: auth.userDetail?.id,
    successHandler: data => {
      dispatcher.updateParticipantData(data);
      if (
        data.ticketStatus === TicketStatus.COMPLETED ||
        (!mountRef.current && isInvoicePaymentPending(data.regPurchaseOrder?.invoicePaymentRequest?.status)) ||
        (!mountRef.current && data.regPurchaseOrder?.hasRequestedOfflinePayment)
      ) {
        dispatcher.updateStep(PurchaseTicketStep.SHOW_CONFIRMATION_VIEW);
        return;
      } else if (isPaymentPending(data.ticketStatus) || isPaymentReceiptPending(data.ticketStatus)) {
        dispatcher.updateStep(PurchaseTicketStep.SHOW_PAYMENT_FORM);
        const purchaseOrderLineObj = data.regPurchaseOrder?.lines.find(i => i.lineType === InventoryType.TICKET);
        if (purchaseOrderLineObj?.couponCode) {
          dispatcher.updateCouponCodeFormValues({
            [CouponCodeFormFieldsName.COUPON_CODE]: purchaseOrderLineObj.couponCode
          });
        }
        return;
      } else updateTicketStatusInDashboardData(queryClient, auth.userDetail?.id, data.ticketStatus);
      if (data.registrationForm.applicationFlow) {
        dispatcher.updateStep(PurchaseTicketStep.SHOW_REG_FORM);
      } else if (data.registrationForm.paymentFlow) {
        if (data.registrationForm.showCouponCode && !data.regPurchaseOrder) {
          dispatcher.updateCouponCodeApplicableFlow(true);
          dispatcher.updateStep(PurchaseTicketStep.SHOW_COUPON_CODE_FORM);
        } else {
          dispatcher.updateStep(PurchaseTicketStep.SHOW_REG_FORM);
        }
      }
    },
    errorHandler: () => {
      dismissPurchaseTicketHandler?.();
    }
  });

  const invoicePaymentRequest = React.useMemo(() => {
    const purchaseAction = participantDetails?.actions?.find(
      action => action.dashboardActionType === DashboardActionType.PURCHASE_TICKET
    );
    return {
      forwardedToInvoicePayerAndPaymentPending: Boolean(
        purchaseAction?.metadata?.forwardedToInvoicePayer &&
          purchaseAction?.metadata?.invoicePaymentRequestStatus === InvoicePaymentRequestStatus.PENDING
      ),
      invoicePayerEmail: purchaseAction?.metadata?.invoicePayerEmail
    };
  }, [participantDetails]);

  const couponCodeSuccessHandler = React.useCallback(
    ({ couponCodeFormValues, applicableRateCardDetails }: CouponCodeSuccessHandlerArgsType) => {
      dispatcher.updateCouponCodeFormValues({
        ...couponCodeFormValues,
        applicableRateCardDetailsByCouponCode: applicableRateCardDetails
      });
      dispatcher.updateStep(PurchaseTicketStep.SHOW_REG_FORM);
    },
    [dispatcher]
  );

  const showApplicableStepOnRegFormSubmit = React.useCallback(
    participantData => {
      if (
        isPaymentPending(participantData.ticketStatus) ||
        isInvoicePaymentPending(participantData.regPurchaseOrder?.invoicePaymentRequest?.status)
      ) {
        mountRef.current = false;
        dispatcher.updateCouponCodeApplicableFlow(false);
        dispatcher.updateStep(PurchaseTicketStep.SHOW_PAYMENT_FORM);
      } else {
        dispatcher.updateStep(PurchaseTicketStep.SHOW_CONFIRMATION_VIEW);
      }
    },
    [dispatcher]
  );

  const initiateRegSuccessHandler = React.useCallback(
    (data?: ApiRegistrationParticipantForParticipantResponse) => {
      if (data) {
        dispatcher.updateParticipantData(data);
        showApplicableStepOnRegFormSubmit(data);
      } else {
        showApplicableStepOnRegFormSubmit(state.participantData);
      }
    },
    [dispatcher, showApplicableStepOnRegFormSubmit, state.participantData]
  );

  const paymentSuccessHandler = React.useCallback(
    (data: ApiPayForPurchaseOrderResponse) => {
      if (!state.participantData) {
        return;
      }
      if (data.ticketStatus !== TicketStatus.COMPLETED) {
        refreshDashboardData(queryClient, auth.userDetail?.id);
        if (data.purchaseOrder.hasRequestedOfflinePayment) {
          mountRef.current = false;
          refetchGetPurchaseTicketForm();
        }
      } else {
        updateTicketStatusInDashboardData(queryClient, auth.userDetail?.id, data.ticketStatus);
      }
      dispatcher.updateStepAndParticipantData({
        stepNo: PurchaseTicketStep.SHOW_CONFIRMATION_VIEW,
        participantData: { ...state.participantData, ...data, regPurchaseOrder: data.purchaseOrder }
      });
    },
    [auth.userDetail, dispatcher, queryClient, state.participantData, refetchGetPurchaseTicketForm]
  );

  const offlineTransactionUpdateSuccessHandler = React.useCallback(
    (purchaseOrder: ApiPurchaseOrder) => {
      dispatcher.updateStepAndParticipantData({
        stepNo: PurchaseTicketStep.SHOW_CONFIRMATION_VIEW,
        participantData: {
          ...state.participantData,
          regPurchaseOrder: purchaseOrder
        } as ApiRegistrationParticipantForParticipantResponse
      });
    },
    [dispatcher, state.participantData]
  );

  const offlineTransactionCancelSuccessHandler = React.useCallback(
    (purchaseOrder: ApiPurchaseOrder) => {
      dispatcher.updateStepAndParticipantData({
        stepNo: PurchaseTicketStep.SHOW_REG_FORM,
        participantData: {
          ...state.participantData,
          regPurchaseOrder: purchaseOrder
        } as ApiRegistrationParticipantForParticipantResponse
      });
      refreshDashboardData(queryClient, auth.userDetail?.id);
    },
    [dispatcher, queryClient, state.participantData, auth.userDetail?.id]
  );

  const paymentErrorHandler = React.useCallback(
    async err => {
      //Payment is no longer valid then close the slider
      if (shouldRefreshPageOnError(err)) {
        dismissPurchaseTicketHandler?.();
        refreshDashboardData(queryClient, auth.userDetail?.id);
      }
    },
    [queryClient, auth.userDetail?.id, dismissPurchaseTicketHandler]
  );

  const paymentMismatchHandler = React.useCallback(() => {
    refetchGetPurchaseTicketForm();
  }, [refetchGetPurchaseTicketForm]);

  const invoicePayerPaymentRequestSuccessHandler = React.useCallback(() => {
    mountRef.current = false;
    refetchGetPurchaseTicketForm();
    refreshDashboardData(queryClient, auth.userDetail?.id);
    dispatcher.updateStep(PurchaseTicketStep.SHOW_CONFIRMATION_VIEW);
  }, [dispatcher, queryClient, auth.userDetail, refetchGetPurchaseTicketForm]);

  const resendInvoicePaymentReminderSuccessHandler = React.useCallback(() => {
    dispatcher.updateStep(PurchaseTicketStep.SHOW_CONFIRMATION_VIEW);
  }, [dispatcher]);

  const isInvoiceOrOfflinePaymentPending = React.useMemo(
    () =>
      (state.participantData?.ticketStatus && isPaymentReceiptPending(state.participantData?.ticketStatus)) ||
      isInvoicePaymentPending(state.participantData?.regPurchaseOrder?.invoicePaymentRequest?.status),
    [state.participantData?.regPurchaseOrder?.invoicePaymentRequest?.status, state.participantData?.ticketStatus]
  );

  return {
    state,
    dispatcher,
    couponCodeSuccessHandler,
    initiateRegSuccessHandler,
    paymentSuccessHandler,
    paymentErrorHandler,
    ticketStatus: participantDetails?.ticketStatus,
    paymentMismatchHandler,
    invoicePayerPaymentRequestSuccessHandler,
    invoicePaymentRequest,
    resendInvoicePaymentReminderSuccessHandler,
    offlineTransactionUpdateSuccessHandler,
    offlineTransactionCancelSuccessHandler,
    isInvoiceOrOfflinePaymentPending
  };
}
